import React, { useState, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Tabs, Tab, Box } from "@material-ui/core";
import {
  DescriptionRounded as PagesIcon,
  MenuBookRounded as NavIcon,
  PublicRounded as SeoIcon,
} from "@material-ui/icons";

import { i18n } from "../../../translate/i18n";
import PagesEditor from "./PagesEditor";
import NavigationEditor from "./NavigationEditor";
import SeoEditor from "./SeoEditor";

// ───── Design tokens (per /tmp/crm-mobile-pattern-reference.md) ─────
// Verbatim from the CRM canonical pages (Tags, Campaigns, Contacts) so the
// SiteContent shell speaks the same visual language as the tenant CRM.
const SP = {
  xxs: 4, xs: 8, sm: 12, md: 16, lg: 20, xl: 24, xxl: 32, hero: 48,
};
const TT = { min: 44, primary: 48, hero: 56 };
const TYPE = {
  caption:    { fontSize: 12, lineHeight: 1.4, letterSpacing: 0.2 },
  body:       { fontSize: 14, lineHeight: 1.5 },
  bodyStrong: { fontSize: 15, lineHeight: 1.45, fontWeight: 500 },
  subheading: { fontSize: 17, lineHeight: 1.4,  fontWeight: 500 },
  heading:    { fontSize: 20, lineHeight: 1.3,  fontWeight: 600 },
  display:    { fontSize: 24, lineHeight: 1.25, fontWeight: 700 },
};
const RAD = { pill: 999, sm: 8, md: 12, lg: 16, xl: 20 };
const COL = {
  brandGreen:     "#25d366",
  brandGreenDeep: "#128c7e",
  brandGreenSoft: "rgba(37,211,102,0.08)",
  textPrimary:    "#0B0D0C",
  textSecondary:  "#52525b",
  borderSoft:     "#eaeaea",
  pageBg:         "#f7faf8",
  cardBg:         "#ffffff",
  destructive:    "#ef4444",
  warning:        "#f59e0b",
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accent     = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const hair       = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg         = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const fgMuted    = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";

  return {
    root: {
      fontFamily: "'Roboto',system-ui,-apple-system,sans-serif",
      color: fg,
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      boxSizing: "border-box",
    },

    // ── Hero header ────────────────────────────────────────────────
    // Card surface that anchors the page. Same hairline + soft shadow
    // language as mainPaper on Tags/Contacts so the admin shell reads
    // as a continuation of the CRM, not a separate product.
    hero: {
      display: "flex",
      flexDirection: "column",
      gap: SP.xs,
      padding: `${SP.xl}px ${SP.xl}px`,
      marginBottom: SP.lg,
      background: isDark ? "#13181a" : "#ffffff",
      border: `1px solid ${hair}`,
      borderRadius: RAD.lg,
      boxShadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px rgba(0,0,0,0.32)"
        : "0 1px 0 rgba(15,22,20,0.02), 0 8px 24px rgba(15,22,20,0.04)",
      [theme.breakpoints.down("xs")]: {
        padding: SP.md,
        marginBottom: SP.md,
        borderRadius: RAD.md,
        gap: SP.xxs,
      },
    },
    // 0.22em accent meta above title — the CRM signature.
    heroAccentMeta: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      margin: 0,
      lineHeight: 1,
    },
    heroTitle: {
      fontFamily: "'Roboto',system-ui,-apple-system,sans-serif",
      fontSize: 24,
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: "-0.01em",
      margin: 0,
      color: fg,
      [theme.breakpoints.down("xs")]: {
        fontSize: 20,
      },
    },
    heroSubtitle: {
      fontFamily: "'Roboto',system-ui,-apple-system,sans-serif",
      fontSize: 14,
      lineHeight: 1.5,
      margin: 0,
      color: isDark ? "rgba(255,255,255,0.55)" : "#52525b",
      maxWidth: 640,
    },

    // ── Pill tab strip ─────────────────────────────────────────────
    // Horizontal-scroll rail with rounded pill tabs. Active = filled
    // brand-green, inactive = transparent with hairline border. The
    // hairline border under the strip ties it back to the hero card.
    tabsWrap: {
      borderBottom: `1px solid ${hair}`,
      marginBottom: SP.lg,
      paddingBottom: SP.xs,
      [theme.breakpoints.down("xs")]: {
        marginBottom: SP.md,
        marginInline: -SP.sm,
        paddingInline: SP.sm,
        paddingBottom: SP.xs,
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        // Hide scrollbar — touch-swipe only, no visible track.
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        "& .MuiTabs-scroller": { overflowX: "auto !important" },
      },
      // Kill the default MUI underline indicator — pills carry the
      // active state via background fill, no underline needed.
      "& .MuiTabs-indicator": { display: "none" },
      "& .MuiTabs-flexContainer": {
        gap: SP.xs,
      },
    },
    tab: {
      fontFamily: "'Roboto',system-ui,-apple-system,sans-serif",
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 1.2,
      textTransform: "none",
      letterSpacing: 0,
      minHeight: TT.min,
      minWidth: 0,
      padding: `${SP.xs}px ${SP.md}px`,
      borderRadius: RAD.pill,
      border: `1px solid ${hair}`,
      background: "transparent",
      color: isDark ? "rgba(255,255,255,0.65)" : "#52525b",
      transition: "background 0.16s ease, color 0.16s ease, border-color 0.16s ease",
      "& .MuiTab-wrapper": {
        flexDirection: "row",
        gap: SP.xs,
        "& > *:first-child": { marginBottom: "0 !important" },
      },
      "&:hover": {
        background: accentSoft,
        color: accent,
        borderColor: accent,
      },
      "&.Mui-selected": {
        background: COL.brandGreenDeep,
        color: "#ffffff",
        borderColor: COL.brandGreenDeep,
        boxShadow: isDark
          ? "0 1px 0 rgba(255,255,255,0.06) inset, 0 4px 12px rgba(0,0,0,0.4)"
          : "0 1px 0 rgba(255,255,255,0.25) inset, 0 4px 12px rgba(18,140,126,0.18)",
      },
      "&.Mui-selected:hover": {
        background: COL.brandGreenDeep,
        color: "#ffffff",
        borderColor: COL.brandGreenDeep,
      },
      [theme.breakpoints.down("xs")]: {
        fontSize: 13,
        padding: `${SP.xs}px ${SP.sm}px`,
        minHeight: TT.min,
      },
    },

    // ── Panel wrapper ──────────────────────────────────────────────
    // Hairline-topped surface that hosts each sub-editor. 16px padding
    // (per brief) gives the sub-editors a consistent gutter without
    // forcing each editor to repeat it.
    panel: {
      borderTop: `1px solid ${hair}`,
      paddingTop: SP.md,
      paddingInline: 0,
      minWidth: 0,
      [theme.breakpoints.down("xs")]: {
        paddingTop: SP.md,
      },
    },
  };
});

// Sub-tab definitions live at module scope so the JSX stays small and the
// ordering is grep-able. `id` doubles as the underlying component key.
const SUBTABS = [
  {
    id: "pages",
    labelKey: "siteContent.tab.pages",
    fallback: "Pages",
    Icon: PagesIcon,
    Component: PagesEditor,
  },
  {
    id: "navigation",
    labelKey: "siteContent.tab.navigation",
    fallback: "Navigation",
    Icon: NavIcon,
    Component: NavigationEditor,
  },
  {
    id: "seo",
    labelKey: "siteContent.tab.seo",
    fallback: "SEO",
    Icon: SeoIcon,
    Component: SeoEditor,
  },
];

const SiteContent = () => {
  const classes = useStyles();
  const [active, setActive] = useState(0);
  const tabsRef = useRef(null);

  // Scroll-to-tab on click — MUI's scrollable tabs auto-scrolls into view on
  // change, but on iOS the focus event sometimes lands BEFORE the layout
  // commit and the indicator slides under the gradient mask. Re-scroll on
  // the next frame so the tap target is always visible.
  useEffect(() => {
    if (!tabsRef.current) return;
    const id = window.requestAnimationFrame(() => {
      const el = tabsRef.current.querySelector(`[data-tab-index="${active}"]`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [active]);

  const ActivePanel = SUBTABS[active]?.Component || (() => null);

  return (
    <div className={classes.root}>
      <header className={classes.hero}>
        <span className={classes.heroAccentMeta}>
          {i18n.t("siteContent.eyebrow", { defaultValue: "SITE CONTENT" })}
        </span>
        <h1 className={classes.heroTitle}>
          {i18n.t("siteContent.title", { defaultValue: "Site Content Manager" })}
        </h1>
        <p className={classes.heroSubtitle}>
          {i18n.t("siteContent.subtitle", {
            defaultValue:
              "Edit marketing pages, navigation, and SEO metadata for whapiz.com.",
          })}
        </p>
      </header>

      <Box ref={tabsRef} className={classes.tabsWrap}>
        <Tabs
          value={active}
          onChange={(_e, v) => setActive(v)}
          variant="scrollable"
          scrollButtons="off"
          allowScrollButtonsMobile={false}
          aria-label={i18n.t("siteContent.tabsLabel", {
            defaultValue: "Site content sections",
          })}
        >
          {SUBTABS.map((t, idx) => {
            const Icon = t.Icon;
            return (
              <Tab
                key={t.id}
                className={classes.tab}
                data-tab-index={idx}
                disableRipple
                label={i18n.t(t.labelKey, { defaultValue: t.fallback })}
                icon={<Icon style={{ fontSize: 16 }} />}
              />
            );
          })}
        </Tabs>
      </Box>

      <div className={classes.panel}>
        <ActivePanel />
      </div>
    </div>
  );
};

export default SiteContent;
